class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.classList.contains('active') ? this.close() : this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.classList.contains('active') ? this.close() : this.open(cartLink);
      }
    });
  }

  queueSwitchingClassRemoval() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => document.body.classList.remove('mth-drawer-switching'));
    });
  }

  // MTH: open the cart the same way as the menu drawer — the panel slides in
  // BELOW the sticky header (which stays visible and, over a transparent hero,
  // whitens via the shared `.section-header.menu-open` class). Reuses the same
  // `--header-bottom-position` / `--viewport-height` custom props the header
  // drawer sets, so the two drawers align pixel-for-pixel.
  syncHeader(isOpen) {
    const header = document.querySelector('.section-header');
    if (isOpen) {
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      if (header) {
        // Match the menu drawer's math exactly (global.js HeaderDrawer): a
        // bordered header pins 1px higher, so the two drawers align pixel-for-pixel.
        const wrapper = document.querySelector('.header-wrapper');
        const borderOffset = wrapper && wrapper.classList.contains('header-wrapper--border-bottom') ? 1 : 0;
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${parseInt(header.getBoundingClientRect().bottom - borderOffset)}px`
        );
        header.classList.add('menu-open');
      }
    } else if (header) {
      // Both drawers share `.section-header.menu-open`; only release it if the
      // menu drawer isn't the one still holding it open (avoid clobbering it).
      const menuDrawerOpen = document.querySelector('header-drawer details[open]');
      if (!menuDrawerOpen) header.classList.remove('menu-open');
    }
  }

  open(triggeredBy, options = {}) {
    if (this.classList.contains('active')) return;
    window.clearTimeout(this.closeTimer);
    this.classList.remove('is-closing');
    const headerDrawer = document.querySelector('header-drawer');
    const menuDetails = headerDrawer?.querySelector('#Details-menu-drawer-container[open]');
    const isSwitch = Boolean(menuDetails) || options.instant;
    if (menuDetails) {
      document.body.classList.add('mth-drawer-switching');
      headerDrawer.closeMenuDrawerInstant(menuDetails.querySelector('summary'));
    }
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.syncHeader(true);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const openDrawer = () => {
      this.classList.add('animate', 'active');
    };
    isSwitch ? openDrawer() : setTimeout(openDrawer);

    const trapDrawerFocus = () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };
    if (isSwitch) {
      trapDrawerFocus();
      this.queueSwitchingClassRemoval();
    } else {
      this.addEventListener('transitionend', trapDrawerFocus, { once: true });
    }

    document.body.classList.add('overflow-hidden');
    document.body.classList.add('cart-drawer-open');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close(options = {}) {
    document.body.classList.remove('cart-drawer-open');
    // Revert the header in LOCKSTEP with the cart-open class (not deferred to the
    // end of the slide-out). Otherwise the floating X un-fixes and jumps back to
    // its header cart-icon slot the instant close starts, while `.menu-open`
    // lingers for the whole animation — so the header churns (icon jump, logo
    // popping back, glyph/colour flicker on the transparent hero) mid-close. The
    // sliding panel covers the header, so the reverted resting header is only
    // revealed as the panel clears it. `keepHeaderOpen` (drawer-to-drawer switch)
    // still skips this so the incoming drawer keeps the header open.
    if (!options.keepHeaderOpen) this.syncHeader(false);

    const finishClose = () => {
      window.clearTimeout(this.closeTimer);
      this.classList.remove('is-closing');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    };

    if (options.instant) {
      this.classList.remove('active', 'is-closing');
      finishClose();
      return;
    }

    if (!this.classList.contains('active')) {
      finishClose();
      return;
    }

    this.classList.add('is-closing');
    this.classList.remove('active');

    const drawerInner = this.querySelector('.drawer__inner');
    const onTransitionEnd = (event) => {
      if (event.target !== drawerInner || event.propertyName !== 'transform') return;
      drawerInner.removeEventListener('transitionend', onTransitionEnd);
      finishClose();
    };

    drawerInner?.addEventListener('transitionend', onTransitionEnd);
    this.closeTimer = window.setTimeout(() => {
      drawerInner?.removeEventListener('transitionend', onTransitionEnd);
      finishClose();
    }, 450);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/*
 * Free-shipping progress meter. The fill's target width comes entirely from the
 * Liquid-set `--fs-target` custom property via CSS, so each freshly-rendered bar
 * paints straight at its value. We deliberately do NOT reset to 0 and replay a
 * 0 → target sweep on connect: the cart drawer replaces this element on every
 * cart update, so that replay fired on every add and stuttered/flickered when
 * the goal was reached (Brandon 2026-07-06). The element stays defined as a
 * no-op so the custom-element upgrade doesn't error.
 */
class FreeShippingBar extends HTMLElement {}
customElements.define('free-shipping-bar', FreeShippingBar);

/*
 * Discount code field for the cart drawer. Applies via the AJAX cart API
 * (POST /cart/update.js with a `discount` field — Shopify Summer '25+) so the
 * customer never leaves the drawer. Applied codes are read back from the AJAX
 * cart JSON (`discount_codes[].applicable`), since Liquid does not reliably
 * expose manually-entered codes pre-checkout. An empty discount clears codes.
 */
class CartDiscount extends HTMLElement {
  connectedCallback() {
    if (!this._init) {
      this._init = true;
      this.input = this.querySelector('[name="discount"]');
      this.applyBtn = this.querySelector('[data-apply]');
      this.errorEl = this.querySelector('[data-error]');
      this.tagsEl = this.querySelector('[data-tags]');

      this.applyBtn?.addEventListener('click', () => this.apply(this.input?.value));
      this.input?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.apply(this.input.value);
        }
      });
      this.input?.addEventListener('input', () => this.setError(''));
      this.tagsEl?.addEventListener('click', (event) => {
        if (event.target.closest('[data-remove]')) this.remove();
      });
    }
    this.hydrate();
  }

  async hydrate() {
    try {
      this.renderTags(await this.fetchCodes());
    } catch (e) {
      /* leave tags as-is on a transient fetch failure */
    }
  }

  async apply(rawCode) {
    const code = (rawCode || '').trim();
    this.setError('');
    if (!code) return;

    this.setBusy(true);
    try {
      await this.postDiscount(code);
      // Read applied codes back from the AJAX cart JSON — the authoritative source
      // for manually-entered codes and their `applicable` flag.
      const codes = await this.fetchCodes();
      const match = codes.find((c) => (c.code || '').toLowerCase() === code.toLowerCase());

      if (match && match.applicable) {
        if (this.input) this.input.value = '';
        // Show the chip on THIS element immediately — if the follow-up section
        // re-render fetch fails, the applied code stays visible instead of
        // vanishing (the code is already applied server-side).
        this.renderTags(codes);
        this.refreshDrawer();
      } else {
        // Clear the just-set (invalid) code so it never reaches checkout.
        await this.postDiscount('');
        this.setError(`"${code}" can't be applied.`);
        this.renderTags([]);
      }
    } catch (e) {
      this.setError('Could not apply that code. Please try again.');
    } finally {
      this.setBusy(false);
    }
  }

  async remove() {
    // Shopify has no per-code AJAX removal; clearing removes all cart codes.
    this.setBusy(true);
    try {
      await this.postDiscount('');
      this.refreshDrawer();
    } catch (e) {
      this.setError('Could not remove that code.');
    } finally {
      this.setBusy(false);
    }
  }

  async postDiscount(code) {
    const response = await fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({ discount: code }),
    });
    if (!response.ok) throw new Error('Discount request failed');
    return response.json();
  }

  async fetchCodes() {
    const response = await fetch(`${routes.cart_url}.js`);
    const cart = await response.json();
    this.currency = cart.currency;

    // The AJAX cart never puts the discounted amount on discount_codes itself —
    // it has to be summed from where Shopify actually allocates it: cart-level
    // (cart_level_discount_applications, e.g. HYPE5's fixed-amount-off-order) or
    // per line (items[].line_level_discount_allocations, for line-scoped codes).
    // Both key off `title`, which matches the code.
    const amounts = new Map();
    (cart.cart_level_discount_applications || []).forEach((application) => {
      amounts.set(application.title, (amounts.get(application.title) || 0) + (application.total_allocated_amount || 0));
    });
    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        const title = allocation.discount_application?.title;
        if (!title) return;
        amounts.set(title, (amounts.get(title) || 0) + (allocation.amount || 0));
      });
    });

    return (cart.discount_codes || []).map((discountCode) => ({
      ...discountCode,
      amount: amounts.get(discountCode.code) || 0,
    }));
  }

  refreshDrawer() {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      // cart-drawer-items re-renders the drawer section (footer + meters + totals);
      // the freshly-rendered cart-discount then re-hydrates its applied-code tags.
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount' });
    } else {
      this.hydrate();
    }
  }

  renderTags(codes) {
    if (!this.tagsEl) return;
    const applicable = (codes || []).filter((c) => c.applicable);
    this.tagsEl.innerHTML = applicable
      .map(
        (c) => `
          <li class="cart-discount__tag">
            <span class="cart-discount__tag-code">${CartDiscount.escape(c.code)}</span>
            ${
              c.amount
                ? `<span class="cart-discount__tag-amount">${CartDiscount.escape(
                    CartDiscount.formatDiscountAmount(c.amount, this.currency)
                  )}</span>`
                : ''
            }
            <button
              type="button"
              class="cart-discount__tag-remove"
              data-remove="${CartDiscount.escape(c.code)}"
              aria-label="Remove discount ${CartDiscount.escape(c.code)}"
            >&times;</button>
          </li>`
      )
      .join('');
    this.classList.toggle('cart-discount--has-codes', applicable.length > 0);
  }

  static formatDiscountAmount(cents, currencyCode) {
    try {
      return `-${new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode || 'USD',
      }).format(cents / 100)}`;
    } catch (e) {
      return `-$${(cents / 100).toFixed(2)}`;
    }
  }

  setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }

  setBusy(isBusy) {
    this.classList.toggle('cart-discount--busy', isBusy);
    if (this.applyBtn) this.applyBtn.disabled = isBusy;
    if (this.input) this.input.disabled = isBusy;
  }

  static escape(str) {
    return String(str).replace(
      /[&<>"']/g,
      (s) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[s])
    );
  }
}
customElements.define('cart-discount', CartDiscount);
